// src/auth/authHelpers.js
// FASE 1.B — Wrappers de Firebase Auth + helpers de error legibles en español.
// FASE 3.5 — Reemplazado el envío automático de Firebase Auth (sendEmailVerification
// / sendPasswordResetEmail del SDK cliente) por Cloud Functions que generan
// el link server-side con admin SDK y lo envían via Resend con templates
// custom. Ver functions/email/.
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { getFirestore, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import app, { functions } from '../firebase';

const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();

// ─── Callables Resend (region southamerica-east1, ver firebase.js) ───
const sendVerificationEmailFn = httpsCallable(functions, 'sendVerificationEmailFn');
const sendPasswordResetEmailFn = httpsCallable(functions, 'sendPasswordResetEmailFn');

// ─── Parser simple de navigator.userAgent → "Browser · OS" ───
// No incluye país (sin geolocalización por IP). Se pasa como `device` al
// callable de password reset para que el email muestre contexto al usuario.
export function detectDevice() {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return null;
  const ua = navigator.userAgent;

  let device = 'Navegador';
  if (/iPhone/i.test(ua)) device = 'iPhone';
  else if (/iPad/i.test(ua)) device = 'iPad';
  else if (/Android/i.test(ua)) device = 'Android';
  else if (/Macintosh|Mac OS X/i.test(ua)) device = 'Mac';
  else if (/Windows/i.test(ua)) device = 'Windows';
  else if (/Linux/i.test(ua)) device = 'Linux';

  let browser = '';
  // El orden importa: Edge contiene "Chrome" en su UA, así que va antes.
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/OPR\/|Opera/i.test(ua)) browser = 'Opera';
  else if (/Firefox/i.test(ua)) browser = 'Firefox';
  else if (/Chrome/i.test(ua)) browser = 'Chrome';
  else if (/Safari/i.test(ua)) browser = 'Safari';

  return browser ? `${device} · ${browser}` : device;
}

function callableErrorToMessage(err) {
  // Las callables tiran HttpsError con `code` (e.g., 'functions/internal').
  // Para el usuario solo mostramos un mensaje genérico — los detalles van a console.
  console.error('[CALLABLE ERROR]', err);
  if (err && err.code === 'functions/unauthenticated') {
    return 'Sesión inválida. Inicia sesión de nuevo.';
  }
  return 'No pudimos enviar el email. Intenta de nuevo en unos minutos.';
}

export async function signupWithEmail(email, password, nombre) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  const finalName = nombre || email.split('@')[0];
  await setDoc(doc(db, 'users', cred.user.uid), {
    email: email,
    nombre: finalName,
    fechaCreacion: serverTimestamp(),
    role: 'client',
    accessUntil: null,
    lastPaymentPlan: null,
    lastPaymentDate: null,
    lastPaymentAmount: null,
    telegramChatId: null,
    telegramLinkCode: null,
    flowCustomerId: null,
    metodoRegistro: 'email',
  });
  // Envío del email de verificación via Cloud Function + Resend.
  // Best-effort: si falla, no rompemos el signup (el usuario puede pedir
  // reenvío desde /verificar-email).
  try {
    await sendVerificationEmailFn({ email, name: finalName });
  } catch (e) {
    console.warn('[signupWithEmail] no se pudo enviar email verificación:', e);
  }
  return cred.user;
}

export async function loginWithEmail(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  return cred.user;
}

export async function loginWithGoogle() {
  const cred = await signInWithPopup(auth, googleProvider);

  // Verificar si el usuario ya existe en Firestore.
  // Si existe (admin / cliente legacy) NO tocamos su doc — las rules bloquean
  // modificar role/accessUntil desde el cliente y romperían el login.
  const userRef = doc(db, 'users', cred.user.uid);
  const snap = await getDoc(userRef);

  if (!snap.exists()) {
    // Usuario NUEVO: crear documento con role=client.
    await setDoc(userRef, {
      email: cred.user.email,
      nombre: cred.user.displayName || cred.user.email.split('@')[0],
      fechaCreacion: serverTimestamp(),
      role: 'client',
      accessUntil: null,
      lastPaymentPlan: null,
      lastPaymentDate: null,
      lastPaymentAmount: null,
      telegramChatId: null,
      telegramLinkCode: null,
      flowCustomerId: null,
      metodoRegistro: 'google',
    });
  }
  // Usuario EXISTENTE: no se toca (preserva role admin/client + accessUntil + etc.).

  return cred.user;
}

// FASE 3.5 — usa el callable Resend en vez de sendPasswordResetEmail del SDK.
// El callable internamente usa admin.auth().generatePasswordResetLink y
// envía via Resend con el template passwordReset (incluye name + device si
// los pasamos). Si no se pasa `name`, el template renderiza '—' en esa fila.
export async function resetPassword(email) {
  const device = detectDevice();
  try {
    await sendPasswordResetEmailFn({ email, device });
  } catch (err) {
    // Convertir HttpsError a un Error con mensaje user-friendly que
    // authErrorMessage va a entender vía fallback.
    throw new Error(callableErrorToMessage(err));
  }
}

// FASE 3.5 — usa el callable Resend para reenviar verificación.
// La callable requiere auth (request.auth.uid) — sólo funciona si hay sesión
// activa. Esto es correcto: la pantalla /verificar-email tiene ProtectedRoute.
export async function resendVerification() {
  if (!auth.currentUser) return;
  const email = auth.currentUser.email;
  if (!email) return;
  // Intentar leer `nombre` desde Firestore para mejor saludo en el email.
  let name = auth.currentUser.displayName || null;
  try {
    const snap = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (snap.exists()) {
      const d = snap.data();
      name = d.nombre || d.displayName || name;
    }
  } catch (_e) {
    // Si Firestore falla, mandamos sin name (template muestra "Hola,").
  }
  try {
    await sendVerificationEmailFn({ email, name });
  } catch (err) {
    throw new Error(callableErrorToMessage(err));
  }
}

// UX audit ISSUE A: limpiar la flag `ee_had_session` ANTES de un logout
// voluntario para evitar que ProtectedRoute interprete `user=null` como
// sesión expirada y muestre el banner amarillo "Tu sesión expiró".
// Para logout voluntario llamar `clearSessionExpiredFlag()` antes de
// `signOut()`. Para sesiones REALMENTE expiradas (token revocado, password
// change remoto), la flag sigue presente y el banner se muestra normal.
export function clearSessionExpiredFlag() {
  try { sessionStorage.removeItem('ee_had_session'); } catch { /* ignore */ }
}

export function authErrorMessage(err) {
  // Loggear error completo a consola para debug
  console.error('[AUTH ERROR]', {
    code: err?.code,
    message: err?.message,
    customData: err?.customData,
    full: err,
  });

  const code = err?.code || '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Email o contraseña incorrectos';
    case 'auth/email-already-in-use':
      return 'Este email ya tiene cuenta';
    case 'auth/weak-password':
      return 'La contraseña debe tener al menos 8 caracteres';
    case 'auth/invalid-email':
      return 'Email inválido';
    case 'auth/too-many-requests':
      return 'Demasiados intentos. Espera unos minutos';
    case 'auth/popup-closed-by-user':
      return 'Cerraste la ventana antes de terminar';
    case 'auth/popup-blocked':
      return 'El navegador bloqueó la ventana. Habilita popups para localhost';
    case 'auth/cancelled-popup-request':
      return 'Hubo otro popup abierto. Intenta de nuevo';
    case 'auth/operation-not-allowed':
      return 'Este método no está habilitado. Activa Google en Firebase Console';
    case 'auth/unauthorized-domain':
      return 'localhost no está en dominios autorizados de Firebase';
    case 'auth/network-request-failed':
      return 'Sin conexión. Revisa tu internet';
    case 'auth/internal-error':
      return 'Error interno de Firebase. Revisá la consola del navegador (F12)';
    default:
      // Si llegamos acá con un Error custom de callable (sin code), usar su
      // message si está, sino el genérico.
      if (err && typeof err.message === 'string' && err.message.length < 200 && !code) {
        return err.message;
      }
      return code ? `Error: ${code}` : 'Error de autenticación desconocido';
  }
}
